<template>
  <div class="app-container">
    <el-alert
      type="info"
      :closable="false"
      show-icon
      title="平台机构管理"
      description="创建教培机构并开通机构管理员账号。机构管理员登录后仅可查看本机构数据；平台超管需切换机构后进入教培业务菜单。"
      class="schools-platform-alert"
    />

    <div class="head-container">
      <div class="crud-opts">
        <span class="crud-opts-left">
          <el-button
            class="filter-item"
            type="primary"
            size="mini"
            icon="el-icon-plus"
            @click="openCreate"
          >
            开户
          </el-button>
        </span>
      </div>

      <div class="filter-container">
        <el-input
          v-model="query.keyword"
          clearable
          size="small"
          placeholder="机构名称/编码"
          class="filter-item"
          style="width: 220px; margin-right: 10px;"
          @keyup.enter.native="loadList"
        />
        <el-button class="filter-item" size="mini" type="primary" icon="el-icon-search" @click="loadList">
          查询
        </el-button>
        <el-button class="filter-item" size="mini" icon="el-icon-refresh-left" @click="resetQuery">
          重置
        </el-button>
      </div>
    </div>

    <el-table v-loading="loading" :data="list" size="small" style="width: 100%;">
      <el-table-column prop="orgName" label="机构名称" min-width="140" show-overflow-tooltip />
      <el-table-column prop="orgCode" label="机构编码" min-width="160" show-overflow-tooltip />
      <el-table-column prop="contactPhone" label="联系电话" width="120" show-overflow-tooltip />
      <el-table-column prop="status" label="状态" width="80" align="center">
        <template slot-scope="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="160" />
      <el-table-column label="操作" width="160" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="mini" @click="openEdit(row)">编辑</el-button>
          <el-button type="text" size="mini" @click="switchToOrg(row)">进入机构</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="query.size"
        :current-page="query.page + 1"
        @current-change="handlePageChange"
      />
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px" @closed="resetForm">
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="110px" size="small">
        <el-form-item label="机构名称" prop="orgName">
          <el-input v-model="form.orgName" placeholder="如：星辰舞蹈培训" />
        </el-form-item>
        <el-form-item label="联系电话" prop="contactPhone">
          <el-input v-model="form.contactPhone" placeholder="选填" />
        </el-form-item>
        <el-form-item label="联系地址" prop="contactAddress">
          <el-input v-model="form.contactAddress" placeholder="选填" />
        </el-form-item>
        <template v-if="!editId">
          <el-divider content-position="left">机构管理员账号</el-divider>
          <el-form-item label="登录账号" prop="adminUsername">
            <el-input v-model="form.adminUsername" placeholder="sys_user 账号" autocomplete="off" />
          </el-form-item>
          <el-form-item label="登录密码" prop="adminPassword">
            <el-input v-model="form.adminPassword" type="password" show-password placeholder="初始密码" autocomplete="new-password" />
          </el-form-item>
          <el-form-item label="管理员昵称" prop="adminNickName">
            <el-input v-model="form.adminNickName" placeholder="选填，默认同机构名" />
          </el-form-item>
        </template>
        <el-form-item v-else label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitting" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { setSchoolsActiveOrgId } from '@/utils/h5-admin-request'
import {
  createSchoolPlatformOrg,
  fetchSchoolPlatformOrgs,
  updateSchoolPlatformOrg
} from '@/api/h5-admin-schools'

export default {
  name: 'SchoolsPlatformOrgs',
  data() {
    return {
      loading: false,
      submitting: false,
      list: [],
      total: 0,
      query: { keyword: '', page: 0, size: 20 },
      dialogVisible: false,
      dialogTitle: '机构开户',
      editId: null,
      form: {
        orgName: '',
        contactPhone: '',
        contactAddress: '',
        adminUsername: '',
        adminPassword: '',
        adminNickName: '',
        status: 1
      },
      formRules: {
        orgName: [{ required: true, message: '请填写机构名称', trigger: 'blur' }],
        adminUsername: [{ required: true, message: '请填写管理员账号', trigger: 'blur' }],
        adminPassword: [{ required: true, message: '请填写初始密码', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    loadList() {
      this.loading = true
      fetchSchoolPlatformOrgs(this.query)
        .then(res => {
          this.list = (res && res.content) || []
          this.total = (res && res.totalElements) || 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    resetQuery() {
      this.query.keyword = ''
      this.query.page = 0
      this.loadList()
    },
    handlePageChange(page) {
      this.query.page = Math.max(page - 1, 0)
      this.loadList()
    },
    openCreate() {
      this.editId = null
      this.dialogTitle = '机构开户'
      this.dialogVisible = true
    },
    openEdit(row) {
      this.editId = row.id
      this.dialogTitle = '编辑机构'
      this.form = {
        orgName: row.orgName,
        contactPhone: row.contactPhone || '',
        contactAddress: row.contactAddress || '',
        status: row.status != null ? row.status : 1
      }
      this.dialogVisible = true
    },
    resetForm() {
      this.editId = null
      this.form = {
        orgName: '',
        contactPhone: '',
        contactAddress: '',
        adminUsername: '',
        adminPassword: '',
        adminNickName: '',
        status: 1
      }
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
      }
    },
    submitForm() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.submitting = true
        const done = () => {
          this.submitting = false
        }
        if (this.editId) {
          updateSchoolPlatformOrg(this.editId, {
            orgName: this.form.orgName,
            contactPhone: this.form.contactPhone,
            contactAddress: this.form.contactAddress,
            status: this.form.status
          })
            .then(() => {
              this.$message.success('已保存')
              this.dialogVisible = false
              this.loadList()
            })
            .finally(done)
        } else {
          createSchoolPlatformOrg(this.form)
            .then(res => {
              this.$message.success('开户成功，管理员账号：' + (res.adminUsername || ''))
              this.dialogVisible = false
              this.loadList()
            })
            .finally(done)
        }
      })
    },
    switchToOrg(row) {
      setSchoolsActiveOrgId(row.id)
      this.$message.success('已切换到：' + row.orgName)
      this.$router.push('/admin/schools/dashboard')
    }
  }
}
</script>

<style scoped lang="less">
.schools-platform-alert {
  margin-bottom: 16px;
}
</style>
